import React, { useState } from 'react';

import { Slider } from '@mui/material';

import NumberInput from 'components/Inputs/NumberInput';
import { MONETARY_PREFIX, removeCurrencySign, removeNonDigits } from 'utils/stringUtils';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import {
  denormalizeLoanAmountSliderValue,
  getSliderMarks,
  normalizeValue,
  roundToStep,
  getMaxLoanAmountLength,
  getMaxSteps,
  getMinSteps,
} from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/loanAmount.utils';
import styles from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/LoanAmount.module.scss';
import { LoanAmountType } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/loanAmount.enums';
import {
  MIN_HIL_LOAN_AMOUNT_RANGE,
  MIN_POS_LOAN_AMOUNT_RANGE,
} from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/LoanAmount/loanAmount.constants';

interface LoanAmountSliderProps {
  setLoanAmountValue: (newValue: number) => void;
  value: number;
  type?: LoanAmountType;
  errorMessage?: string;
}

const LoanAmount = ({ setLoanAmountValue, value, type = LoanAmountType.Full, errorMessage }: LoanAmountSliderProps) => {
  const [sliderValue, setSliderValue] = useState(normalizeValue(value));
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const denormalizedValue = denormalizeLoanAmountSliderValue(newValue as number);
    let roundedValue = roundToStep(denormalizedValue);

    if (roundedValue === MIN_HIL_LOAN_AMOUNT_RANGE && type === LoanAmountType.PointOfSale) {
      roundedValue = MIN_POS_LOAN_AMOUNT_RANGE;
    }

    setSliderValue(normalizeValue(roundedValue));
    setLoanAmountValue(roundedValue);
  };

  const handleLoanAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +removeNonDigits(removeCurrencySign(event.target.value));

    setLoanAmountValue(newValue);

    setSliderValue(normalizeValue(newValue));
  };

  return (
    <div className={styles.loanAmountContainer}>
      <NumberInput
        label={'How much do you want to borrow?'}
        prefix={MONETARY_PREFIX}
        placeholder={MONETARY_PREFIX}
        thousandSeparator
        inputContainerClassName={styles.loanAmountInputContainer}
        inputClassName={styles.loanAmountInput}
        labelClassName={styles.loanAmountLabel}
        name={LoanInformationVariables.LoanAmount}
        onChange={handleLoanAmountInputChange}
        errorMessage={errorMessage}
        value={value}
        maxLength={getMaxLoanAmountLength(type)}
        allowNegative={false}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
      />
      <Slider
        aria-label="Loan Amount"
        value={sliderValue}
        step={1}
        marks={getSliderMarks(type)}
        min={getMinSteps(type)}
        max={getMaxSteps(type)}
        classes={{
          root: styles.loanAmountSlider,
          mark: styles.loanAmountSliderMark,
          markLabel: styles.loanAmountSliderMarkLabel,
          rail: styles.loanAmountSliderRail,
          thumb: styles.loanAmountSliderThumb,
        }}
        valueLabelDisplay="off"
        onChange={handleSliderChange}
        valueLabelFormat={(labelValue) =>
          `$${Math.round(denormalizeLoanAmountSliderValue(labelValue)).toLocaleString('en-US')}`
        }
      />
    </div>
  );
};

export default LoanAmount;
